import React, { useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { IoIosRemoveCircle } from "react-icons/io";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useModuleContext } from "../../Context/ModuleContext";
import {
  faBook,
  faQuestionCircle,
  faCloudUploadAlt,
  faFilePdf,
  faFileWord,
} from "@fortawesome/free-solid-svg-icons";

import VisibilityButton from "../VisibilityButton";
import DeleteConfirmationModal from "../DeleteConfirmationModal";
import EditableTitle from "../EditableTitle";
import DragHandle from "../DragHandle";
import Notification from "../Notification/Notification";
import { useTranslation } from "react-i18next"; // Importer useTranslation

const LessonItem = ({ lesson }) => {
  const { t } = useTranslation(); // Assigner useTranslation à {t}
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: lesson.id });

  const [notificationParams, setNotificationParams] = useState({
    show: false,
    message: t("notification_default_message"),
    bg: "danger",
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [visible, setVisible] = useState(lesson.is_visible);
  const [editIndex, setEditIndex] = useState(null);
  const [editTitle, setEditTitle] = useState(lesson.title);
  const [isHovered, setIsHovered] = useState(false);
  const [isTitleHovered, setIsTitleHovered] = useState(false); // État pour gérer le survol du titre
  const { refreshModule } = useModuleContext();

  const handleDoubleClick = () => {
    setEditIndex(lesson.id);
    setEditTitle(lesson.title);
  };

  const handleTitleChange = (e) => {
    setEditTitle(e.target.value);
  };

  const handleBlur = async (newTitle) => {
    if (newTitle.trim() === "") {
      setNotificationParams({
        show: true,
        message: t("empty_title_error"),
        bg: "danger",
      });
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}lessons/${lesson.id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ title: newTitle }),
        }
      );

      if (!response.ok) {
        throw new Error(t("update_title_error"));
      }

      console.log(t("title_updated_success"));
      setEditIndex(null);
      refreshModule();
    } catch (err) {
      console.error(t("update_title_error"), err);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleBlur(editTitle);
    }
  };

  const style = {
    transform: transform ? CSS.Transform.toString(transform) : undefined,
    transition,
    padding: "8px",
    backgroundColor: "#f8f9fa",
    border: "1px solid #ddd",
    borderRadius: "4px",
    marginBottom: "4px",
    cursor: "grab",
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleSetVisible = async (newValue) => {
    setVisible(newValue);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}lessons/${lesson.id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ is_visible: newValue }),
        }
      );

      if (!response.ok) {
        throw new Error(t("update_visibility_error"));
      }

      refreshModule();
      console.log(t("visibility_updated_success"));
    } catch (err) {
      console.error(t("update_visibility_error"), err);
    }
  };

  const handleDeleteLesson = async (lessonId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}lessons/${lessonId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ is_deleted: true, is_visible: false }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || t("delete_lesson_error"));
      }

      console.log(t("lesson_deleted_success"));
      refreshModule();
    } catch (err) {
      console.error(t("delete_lesson_error"), err);
    }
  };

  const getIconForLessonType = (type) => {
    switch (type) {
      case t("theory"):
        return faBook;
      case t("quiz"):
        return faQuestionCircle;
      case t("upload"):
        return faCloudUploadAlt;
      case t("pdf"):
        return faFilePdf;
      case t("word"):
        return faFileWord;
      default:
        return null;
    }
  };

  return (
    <>
      <div
        ref={setNodeRef}
        className="card mb-2"
        style={{
          transform: transform ? CSS.Transform.toString(transform) : undefined,
          transition,
        }}
      >
        <div
          className="card-header d-flex justify-content-between align-items-center py-2"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="d-flex gap-2 align-items-center">
            {isHovered && (
              <div
                style={{
                  opacity: isHovered ? 1 : 0,
                  transition: "opacity 0.2s ease",
                }}
              >
                <DragHandle listeners={listeners} attributes={attributes} />
              </div>
            )}

            <FontAwesomeIcon icon={getIconForLessonType(lesson.type)} />

            <div
              onMouseEnter={() => setIsTitleHovered(true)} // Affiche le texte au survol
              onMouseLeave={() => setIsTitleHovered(false)} // Cache le texte lorsque la souris quitte
              className="d-flex align-items-center"
            >
              <EditableTitle
                title={editTitle}
                onEdit={handleDoubleClick}
                onSave={handleBlur}
                onChange={handleTitleChange}
                itemType="lesson"
              />
              {isTitleHovered && (
                <span className="text-muted ms-2">
                  {t("double_click_to_edit")}
                </span>
              )}
            </div>
          </div>

          <div
            className="d-flex gap-2 align-items-center"
            style={{
              opacity: isHovered ? 1 : 0,
              transition: "opacity 0.2s ease",
              visibility: isHovered ? "visible" : "hidden",
            }}
          >
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="button-tooltip">{t("delete")}</Tooltip>}
            >
              <button
                className="btn btn-danger btn-sm"
                onClick={() => setModalOpen(true)}
                id={`tooltip-delete-${lesson.id}`}
              >
                <IoIosRemoveCircle />
              </button>
            </OverlayTrigger>

            <VisibilityButton
              isVisible={visible}
              onClick={() => {
                handleSetVisible(!visible);
              }}
              itemType="lesson"
            />
          </div>
        </div>
      </div>

      <DeleteConfirmationModal
        item={lesson}
        itemType="lesson"
        show={modalOpen}
        onClose={handleModalClose}
        onDelete={handleDeleteLesson}
      />
    </>
  );
};

export default LessonItem;
